/*
 * Anagen Black Friday automatic free gift (anagen free brush).
 *
 * What this controls (storefront presentation + Ajax cart actions):
 *  - Adds exactly one gift line when the cart total after discounts (excluding the gift) reaches the
 *    gift threshold, and removes it as soon as the cart falls below it.
 *  - Keeps the gift at quantity 1 and collapses duplicate gift lines.
 *  - Blocks the gift variant from being added by any storefront form, button or script except this one.
 *  - Labels the gift line "FREE GIFT" and hides its quantity / remove controls.
 *  - Keeps every rewards panel (cart drawer and full cart page) in step with the cart: a change made in
 *    one view only re-renders that view, so any panel left out of date is re-rendered from
 *    snippets/an-bf-cart. No thresholds or reward logic live in this file.
 *
 * What Shopify does (not this file): the gift's EGP 0 price is its real Shopify price, checkout charges
 * nothing for it, and inventory is deducted when the order is placed.
 *
 * Limitation (accepted): theme code cannot stop direct requests to Shopify's cart API or cart permalinks
 * made outside this storefront. A Shopify Flow workflow is the backstop for those orders.
 */
(() => {
  const cfg = window.anBfRewards;
  if (!cfg || !cfg.giftVariantId || window.__anBfGift) return;
  window.__anBfGift = true;

  const GIFT_ID = Number(cfg.giftVariantId);
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const RETRY_AFTER_MS = 30 * 1000; // after a rejected add, wait before trying again (per page load)
  const MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const ADD = /\/cart\/add(\.js|\.json)?(\?|$)/;
  const nativeFetch = window.fetch.bind(window);

  /* ---------- helpers ---------- */
  const isGiftId = (value) => Number(value) === GIFT_ID;
  const urlOf = (input) => (typeof input === 'string' ? input : (input && input.url) || '');

  // Diagnostics, readable in the browser console with: anBfGiftDebug()
  const state = { lastCheck: null, lastAddError: null, retryAt: 0 };
  const log = (...args) => console.info('[anagen-gift]', ...args);
  window.anBfGiftDebug = () => ({ config: { ...cfg }, ...state });

  function addRecentlyFailed() {
    return state.retryAt > Date.now();
  }

  // Does a cart add/update body try to add the gift variant?
  function bodyAddsGift(body) {
    if (!body) return false;
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (isGiftId(body.get('id'))) return true;
        for (const [key, value] of body.entries()) {
          if (/^items\[\d+\]\[id\]$/.test(key) && isGiftId(value)) return true;
          if ((key === `updates[${GIFT_ID}]`) && Number(value) > 0) return true;
        }
        return false;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return bodyAddsGift(Object.fromEntries(body.entries()));
      }
      let data = body;
      if (typeof body === 'string') {
        const trimmed = body.trim();
        if (trimmed.startsWith('{')) data = JSON.parse(trimmed);
        else data = Object.fromEntries(new URLSearchParams(trimmed).entries());
      }
      if (!data || typeof data !== 'object') return false;
      if (isGiftId(data.id) && Number(data.quantity === undefined ? 1 : data.quantity) > 0) return true;
      if (Array.isArray(data.items) && data.items.some((item) => item && isGiftId(item.id) && Number(item.quantity === undefined ? 1 : item.quantity) > 0)) return true;
      if (data.updates && !Array.isArray(data.updates) && Number(data.updates[GIFT_ID]) > 0) return true;
      if (Object.keys(data).some((key) => key === `updates[${GIFT_ID}]` && Number(data[key]) > 0)) return true;
    } catch (e) { /* unknown body shape: let it through, the sync will correct the cart */ }
    return false;
  }

  function blockedResponse() {
    const message = cfg.giftBlocked || 'This item is a free gift and cannot be added on its own.';
    return new Response(JSON.stringify({ status: 422, message, description: message }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /* ---------- intercept storefront cart requests ---------- */
  window.fetch = function anBfFetch(input, init) {
    const url = urlOf(input);
    if (MUTATION.test(url)) {
      const body = init && init.body;
      if (ADD.test(url) && bodyAddsGift(body)) return Promise.resolve(blockedResponse());
      if (/\/cart\/update/.test(url) && bodyAddsGift(body)) return Promise.resolve(blockedResponse());
      return nativeFetch(input, init).then((response) => {
        scheduleSync();
        return response;
      });
    }
    return nativeFetch(input, init);
  };

  const NativeXHR = window.XMLHttpRequest;
  if (NativeXHR) {
    const open = NativeXHR.prototype.open;
    const send = NativeXHR.prototype.send;
    NativeXHR.prototype.open = function (method, url) {
      this.__skUrl = String(url || '');
      return open.apply(this, arguments);
    };
    NativeXHR.prototype.send = function (body) {
      const url = this.__skUrl || '';
      if (MUTATION.test(url)) {
        if ((ADD.test(url) || /\/cart\/update/.test(url)) && bodyAddsGift(body)) {
          this.__skUrl = '';
          const xhr = this;
          setTimeout(() => {
            Object.defineProperty(xhr, 'readyState', { configurable: true, value: 4 });
            Object.defineProperty(xhr, 'status', { configurable: true, value: 422 });
            Object.defineProperty(xhr, 'responseText', { configurable: true, value: JSON.stringify({ status: 422, description: cfg.giftBlocked }) });
            xhr.dispatchEvent(new Event('readystatechange'));
            xhr.dispatchEvent(new Event('load'));
            xhr.dispatchEvent(new Event('loadend'));
          });
          return undefined;
        }
        this.addEventListener('loadend', scheduleSync);
      }
      return send.apply(this, arguments);
    };
  }

  // Classic (non-Ajax) add-to-cart forms and on-site cart permalinks for the gift.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!ADD.test(form.getAttribute('action') || '')) return;
    const idField = form.querySelector('[name="id"]');
    if (idField && isGiftId(idField.value)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest(`a[href*="/cart/${GIFT_ID}"]`);
    if (link) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);

  /* ---------- cart sync ---------- */
  let running = null;
  let again = false;
  let timer = 0;

  function scheduleSync(delay = 120) {
    clearTimeout(timer);
    timer = setTimeout(sync, delay);
  }

  function sync() {
    if (running) { again = true; return running; }
    running = (async () => {
      do {
        again = false;
        try { await syncOnce(); } catch (e) { console.warn('[anagen-gift]', e); }
      } while (again);
    })().finally(() => { running = null; });
    return running;
  }

  async function getCart() {
    const response = await nativeFetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    return response.json();
  }

  async function postJson(path, payload) {
    return nativeFetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  async function syncOnce() {
    const cart = await getCart();
    const giftLines = cart.items.filter((item) => item.variant_id === GIFT_ID);
    const paidLines = cart.items.filter((item) => item.variant_id !== GIFT_ID);
    const giftQty = giftLines.reduce((sum, item) => sum + item.quantity, 0);
    const giftTotal = giftLines.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
    const eligibleTotal = Math.max(0, cart.total_price - giftTotal);
    const qualifies = cfg.active === true && paidLines.length > 0 && eligibleTotal >= Number(cfg.giftThreshold);
    state.lastCheck = {
      campaignActive: cfg.active === true,
      eligibleTotal: eligibleTotal / 100,
      threshold: Number(cfg.giftThreshold) / 100,
      qualifies,
      giftQuantityInCart: giftQty,
      at: new Date().toISOString(),
    };

    let changed = false;

    if (!qualifies && giftQty > 0) {
      // Lost eligibility (or campaign over): remove every gift line.
      const updates = {};
      giftLines.forEach((item) => { updates[item.key] = 0; });
      await postJson('cart/update.js', { updates });
      changed = true;
    } else if (qualifies && (giftQty > 1 || giftLines.length > 1)) {
      // Duplicates or quantity raised: keep exactly one.
      const updates = {};
      giftLines.forEach((item, index) => { updates[item.key] = index === 0 ? 1 : 0; });
      await postJson('cart/update.js', { updates });
      changed = true;
    } else if (qualifies && giftQty === 0) {
      if (addRecentlyFailed()) {
        markGiftUnavailable();
      } else {
        const response = await postJson('cart/add.js', {
          items: [{ id: GIFT_ID, quantity: 1, properties: { _anagen_gift: '1' } }],
        });
        if (response.ok) {
          state.lastAddError = null;
          state.retryAt = 0;
          log(`Added the free gift (variant ${GIFT_ID}) at ${eligibleTotal / 100} EGP after discount.`);
          changed = true;
        } else {
          // Shopify rejected the gift: out of stock, or the product is Draft / not published to the
          // Online Store (the storefront cart API cannot see unpublished products).
          let detail = '';
          try { const body = await response.json(); detail = body.description || body.message || ''; } catch (e) { /* no body */ }
          state.lastAddError = { status: response.status, detail, at: new Date().toISOString() };
          state.retryAt = Date.now() + RETRY_AFTER_MS;
          console.warn(`[anagen-gift] Shopify rejected the free gift (variant ${GIFT_ID}): HTTP ${response.status} ${detail}. ` +
            'If the brush is Draft or not published to the Online Store sales channel, Shopify cannot add it to a cart.');
          markGiftUnavailable();
        }
      }
    }

    if (changed) await refreshCartUI();
    else await refreshStaleRewards(eligibleTotal, giftQty > 0);
    decorateGiftLines();
  }

  /* ---------- UI ---------- */
  function markGiftUnavailable() {
    document.querySelectorAll('[data-an-bf-rewards]').forEach((block) => {
      block.dataset.giftState = 'unavailable';
      const reward = block.querySelector('[data-an-bf-gift-reward]');
      const message = block.querySelector('[data-an-bf-gift-message]');
      if (reward) reward.classList.add('is-unavailable');
      if (message && cfg.giftUnavailable) message.textContent = cfg.giftUnavailable;
    });
  }

  async function refreshCartUI() {
    const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');
    const sections = ['cart-icon-bubble'];
    if (drawerInner) sections.push('cart-drawer');
    if (cartItems && cartItems.dataset.id) sections.push(cartItems.dataset.id);
    if (cartFooter && cartFooter.dataset.id) sections.push(cartFooter.dataset.id);

    const response = await nativeFetch(`${window.location.pathname}?sections=${sections.join(',')}`, { cache: 'no-store' });
    if (!response.ok) return;
    const html = await response.json();
    const parse = (markup) => new DOMParser().parseFromString(markup, 'text/html');

    if (drawerInner && html['cart-drawer']) {
      const next = parse(html['cart-drawer']).querySelector('#CartDrawer .drawer__inner');
      if (next) drawerInner.innerHTML = next.innerHTML;
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', !!parse(html['cart-drawer']).querySelector('.drawer__inner-empty'));
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && html['cart-icon-bubble']) {
      const next = parse(html['cart-icon-bubble']).querySelector('.shopify-section');
      if (next) bubble.innerHTML = next.innerHTML;
    }
    [[cartItems, cartItems && cartItems.dataset.id], [cartFooter, cartFooter && cartFooter.dataset.id]].forEach(([el, id]) => {
      if (!el || !id || !html[id]) return;
      const target = el.querySelector('.js-contents');
      const next = parse(html[id]).querySelector('.js-contents');
      if (target && next) target.innerHTML = next.innerHTML;
    });
  }

  // Each rewards panel carries data-an-bf-state="<total after discount, excluding the gift>:<gift in bag>".
  // Re-render, from the theme's own sections, only the panels whose state no longer matches the cart.
  async function refreshStaleRewards(eligibleTotal, giftInCart) {
    const expected = `${eligibleTotal}:${giftInCart}`;
    const stale = Array.from(document.querySelectorAll('[data-an-bf-rewards][data-an-bf-state]'))
      .filter((panel) => panel.dataset.anBfState !== expected);
    if (!stale.length) return;

    const pageItems = document.getElementById('main-cart-items');
    const pageSection = pageItems && pageItems.dataset.id;
    const sections = [];
    if (document.getElementById('CartDrawer')) sections.push('cart-drawer');
    if (pageSection) sections.push(pageSection);
    if (!sections.length) return;

    const response = await nativeFetch(`${window.location.pathname}?sections=${sections.join(',')}`, { cache: 'no-store' });
    if (!response.ok) return;
    const html = await response.json();
    stale.forEach((panel) => {
      const placement = panel.dataset.anBfPlacement || 'drawer';
      const source = placement === 'page' ? html[pageSection] : html['cart-drawer'];
      if (typeof source !== 'string') return;
      const next = new DOMParser().parseFromString(source, 'text/html')
        .querySelector(`[data-an-bf-rewards][data-an-bf-placement="${placement}"]`);
      if (next) panel.replaceWith(next);
      else panel.remove(); // the cart is now empty, so that view no longer shows rewards
    });
  }

  function decorateGiftLines() {
    document.querySelectorAll(`input[data-quantity-variant-id="${GIFT_ID}"]`).forEach((input) => {
      const row = input.closest('.cart-item, tr, li');
      if (!row || row.dataset.anBfGift === '1') return;
      row.dataset.anBfGift = '1';
      row.classList.add('an-bf-gift-line');
      const name = row.querySelector('.cart-item__name');
      if (name && !row.querySelector('.an-bf-gift-tag')) {
        const tag = document.createElement('span');
        tag.className = 'an-bf-gift-tag';
        tag.textContent = ` — ${cfg.giftLabel || 'FREE GIFT'}`;
        name.appendChild(tag);
        name.removeAttribute('href');
      }
      row.querySelectorAll('a.cart-item__link').forEach((link) => link.removeAttribute('href'));
    });
  }

  // Re-label after the theme re-renders the drawer or cart page.
  const observer = new MutationObserver(() => decorateGiftLines());
  const startObserver = () => {
    document.querySelectorAll('cart-drawer, #main-cart-items').forEach((el) => observer.observe(el, { childList: true, subtree: true }));
  };

  // The theme's pub/sub helpers are top-level globals (PUB_SUB_EVENTS is a const, not on window).
  let subscribed = false;
  const subscribeToTheme = () => {
    if (subscribed) return;
    /* global subscribe, PUB_SUB_EVENTS */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleSync());
      subscribed = true;
    }
  };
  document.addEventListener('cartUpdated', () => scheduleSync());
  window.addEventListener('pageshow', (event) => { if (event.persisted) scheduleSync(0); });
  window.addEventListener('load', subscribeToTheme);

  const start = () => {
    log(`Loaded. Campaign active: ${cfg.active === true}; gift variant ${GIFT_ID}; unlocks at ${Number(cfg.giftThreshold) / 100} EGP after discount.`);
    subscribeToTheme(); startObserver(); decorateGiftLines(); sync();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
